import type { HTMLAttributes, Ref } from "react";

import { stackAlign, stackGap } from "~/components/Stack.css";

export type StackGap =
  | "none"
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl";

export type StackAlign = "start" | "center" | "end" | "stretch";

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  /** Gap between children. Defaults to "md" (16px). */
  gap?: StackGap;
  /** Cross-axis alignment. Defaults to "stretch" (full-width children). */
  align?: StackAlign;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Vertical layout primitive. Renders a <div> with flex column layout
 * and a token-driven gap. Use for any "column of things" — form
 * fields, card lists, page sections.
 *
 * Prefer Stack over manual margin-top rules in child elements. The
 * gap model keeps spacing consistent and avoids the "first-child
 * margin collapse" class of bugs.
 */
export function Stack({
  gap = "md",
  align = "stretch",
  className,
  ref,
  ...rest
}: StackProps) {
  const variant = `${stackGap[gap]} ${stackAlign[align]}`;
  const merged =
    className !== undefined && className.length > 0
      ? `${variant} ${className}`
      : variant;
  return <div ref={ref} className={merged} {...rest} />;
}
